import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowDownIcon } from "../../icons";
import * as Popover from "@radix-ui/react-popover";
import { useUser } from "@/contexts/UserContext";
import { useTranslation } from "react-i18next";

interface MenuItem {
  label: string;
  onClick: () => void;
  variant: "default" | "danger";
  icon?: React.ReactNode;
}

export const AdminHeaderPopover: React.FC = () => {
  const { t } = useTranslation();
  const { user, logout } = useUser();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const getInitials = (name: string | undefined) => {
    if (!name) return "?";

    const nameParts = name.trim().split(" ");

    if (nameParts.length >= 2) {
      // İsim ve soyisim varsa, her ikisinin baş harfini al
      return (nameParts[0][0] + nameParts[1][0]).toUpperCase();
    } else {
      // Sadece isim varsa, ilk iki harfi al
      return name.slice(0, 2).toUpperCase();
    }
  };

  const menuItems: MenuItem[] = [
    {
      label: t("navbar.accountSettings"),
      onClick: () => navigate("/admin/profile"),
      variant: "default",
    },
    {
      label: t("navbar.logout"),
      onClick: handleLogout,
      variant: "danger",
    },
  ];

  const getItemClasses = (variant: "default" | "danger") => {
    const baseClasses =
      "w-full cursor-pointer p-2 text-start rounded transition-all duration-200";
    const variantClasses = {
      default: "hover:bg-white/10 dark:hover:bg-gray-700/50",
      danger:
        "hover:bg-red-500/20 text-red-400 dark:text-red-500 font-bold hover:text-red-300",
    };
    return `${baseClasses} ${variantClasses[variant]}`;
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button className="group flex cursor-pointer items-center gap-3">
          <div className="w-8 h-8 bg-ds-accent grid place-items-center rounded-full text-sm font-bold text-[#0E131F] shadow-lg">
            {getInitials(user?.name)}
          </div>
          <div className="hidden sm:block text-left">
            {user?.name ? (
              <div className="text-sm font-medium text-white">{user.name}</div>
            ) : (
              <div className="h-3 w-20 bg-gray-600 rounded animate-pulse" />
            )}
          </div>
          <ArrowDownIcon className="w-3 text-neutral-300 group-data-[state=open]:rotate-180 transition-transform" />
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="end"
          sideOffset={8}
          alignOffset={-16}
          className="z-[9999] w-[180px] rounded-md border-[0.4px] border-[#E4E4E4] bg-[#242F46] dark:bg-gray-800 p-2 text-[13px] text-neutral-50"
        >
          {menuItems.map((item, index) => (
            <button
              key={index}
              onClick={() => {
                item.onClick();
                setOpen(false);
              }}
              className={`${getItemClasses(item.variant)} ${
                item.icon ? "flex items-center gap-2" : ""
              }`}
            >
              {item.icon && item.icon}
              {item.label}
            </button>
          ))}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
